import { useCallback, useEffect, useRef } from 'react';
import { driver, type Driver } from 'driver.js';
import type { PasoGuia } from '@/features/guia-interactiva/types/guia-interactiva.types';

const ESPERA_ELEMENTO_MS = 5000;

function esperarElemento(
  selector: string,
  timeoutMs = ESPERA_ELEMENTO_MS
): Promise<Element | null> {
  return new Promise(resolve => {
    const hit = document.querySelector(selector);
    if (hit) return resolve(hit);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);
  });
}

export interface EjecutarTourOpciones {
  pasos: PasoGuia[];
  permitirCerrar: boolean;
  onCompletar?: () => void;
  onCerrar?: () => void;
}

/**
 * Envoltura mínima sobre driver.js para ejecutar un único tour contextual
 * (sin navegación entre rutas: cada tour vive en su pantalla). Devuelve
 * `ejecutar` y `cerrar` estables y destruye la instancia al desmontar.
 */
export function useDriver() {
  const driverRef = useRef<Driver | null>(null);

  useEffect(
    () => () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    },
    []
  );

  const ejecutar = useCallback(async (opts: EjecutarTourOpciones) => {
    const { pasos, permitirCerrar, onCompletar, onCerrar } = opts;
    if (pasos.length === 0) return;

    if (driverRef.current) {
      driverRef.current.destroy();
    }

    const primerElemento = await esperarElemento(pasos[0]!.selector);
    if (!primerElemento) return;

    // Filtrar pasos cuyo elemento no existe en el DOM en este momento. Algunos
    // pasos apuntan a botones condicionales (ej. 'Publicar' solo aparece cuando
    // la tecnologia esta en pendiente_aprobacion). Si el selector no matchea,
    // driver.js renderiza el popover flotando y confunde; mejor saltear.
    const pasosVisibles = pasos.filter(p => document.querySelector(p.selector) !== null);
    if (pasosVisibles.length === 0) return;

    let completadoRef = false;

    const steps = pasosVisibles.map(p => ({
      element: p.selector,
      popover: {
        title: p.titulo,
        description: p.descripcion,
        side: p.side ?? 'bottom',
        align: p.align ?? 'start',
      },
    }));

    const d = driver({
      showProgress: true,
      allowClose: permitirCerrar,
      // Click en el overlay (fondo oscuro) NO cierra ni avanza. Previene
      // cierres accidentales; el usuario debe usar Siguiente / Anterior o
      // el botón X (solo visible cuando `allowClose` está activo).
      overlayClickBehavior: () => {},
      // Tecla Esc sigue el mismo comportamiento que el botón X: si el tour
      // es bloqueante (`permitirCerrar: false`), Esc tampoco cierra. Esto
      // desactiva también la navegación por flechas, pero los tours son
      // cortos (≤5 pasos) y los botones son suficientes.
      allowKeyboardControl: permitirCerrar,
      overlayOpacity: 0.5,
      stagePadding: 6,
      smoothScroll: true,
      popoverClass: 'guia-interactiva-popover',
      nextBtnText: 'Siguiente',
      prevBtnText: 'Anterior',
      doneBtnText: 'Finalizar',
      progressText: 'Paso {{current}} de {{total}}',
      steps,
      onNextClick: (_el, _step, { driver: dv, state }) => {
        const next = (state.activeIndex ?? 0) + 1;
        if (next < pasosVisibles.length) {
          dv.moveNext();
          return;
        }
        completadoRef = true;
        dv.destroy();
      },
      onPrevClick: (_el, _step, { driver: dv }) => {
        dv.movePrevious();
      },
      onDestroyed: () => {
        driverRef.current = null;
        if (completadoRef) {
          onCompletar?.();
        } else {
          onCerrar?.();
        }
      },
    });

    driverRef.current = d;
    d.drive();
  }, []);

  const cerrar = useCallback(() => {
    driverRef.current?.destroy();
  }, []);

  return { ejecutar, cerrar };
}
